import { z } from 'zod';
import { Ulid, UserIdentity } from './common.js';
import { Uuid } from './analytics.js';
/**
 * Custom game events (`docs/game-events-plan.md`).
 *
 * The events a game's own code reports — a level attempted, a hero picked, a match won — which
 * BoardFlight stores per game and charts by *kind*. Not to be confused with `events.ts`, which is
 * install telemetry from the BoardFlight client and predates this module; the plan's `events.ts`
 * is this file.
 *
 * Several things that are not this module have to agree on these rules: the Worker that validates
 * a catalog and an ingest batch, the dashboard's catalog editor, the SDK in `@boardflight/game`,
 * the injected layer, agentd's `events` tools and the CLI. So the rules are written once, here, and
 * the linter that agents, the CLI and the dashboard all run is here too (§11.1).
 */
/* --------------------------------------------------------------------------------- limits */
/**
 * An event name: a stable identifier, safe in a URL and a SQL literal (§5.4).
 *
 * Lower snake case only. A name is what a report is addressed by
 * (`#channels/<slug>/events/<name>`), so it should read the same everywhere it appears.
 */
export const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
/** A property name. Camel case is allowed, because game code is mostly JavaScript and C#. */
export const EVENT_PROP_PATTERN = /^[a-z][a-zA-Z0-9_]{0,39}$/;
/**
 * **A string value is an identifier, never a sentence** (§5.4) — and this is the load-bearing rule
 * of the whole feature.
 *
 * No spaces, no punctuation beyond `_.:-`, 48 characters. It makes it structurally impossible to
 * store a sentence a stranger typed, which is why `analytics:read` may sit beside write scopes
 * while `feedback:read` may not (decision 9, `CLAUDE.md`). A game that wants *Fire Mage* on a chart
 * sends `fire_mage` and labels it in the catalog. Loosening this reopens that decision.
 */
export const EVENT_STRING_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:\-]{0,47}$/;
/** Properties per event, and context entries per event. Bounded rows. */
export const MAX_EVENT_PROPS = 16;
/** `|x|` for a number property. Finite, and small enough to sum without losing the units digit. */
export const MAX_EVENT_NUMBER = 1e12;
/** Events one game may declare, and distinct names one game may send (§5.4). */
export const MAX_EVENT_NAMES_PER_GAME = 200;
/** Events in one ingest request (§7.3). */
export const MAX_GAME_EVENTS_PER_REQUEST = 50;
/**
 * Per install, per hour (§8.2). Its own constant, deliberately not sessions'
 * `MAX_EVENTS_PER_INSTALL_PER_HOUR` (120): a game reports far more of these than heartbeats.
 */
export const MAX_GAME_EVENTS_PER_INSTALL_PER_HOUR = 1_200;
/** How often the layer sends what it has queued (§7.2), and how many events make it send sooner. */
export const GAME_EVENTS_FLUSH_SECONDS = 10;
export const GAME_EVENTS_FLUSH_AT = 25;
/**
 * Events the layer keeps on the device while it cannot send — a Board offline for an evening still
 * reports its playtest when it comes back (§7.3). Past this, the oldest go.
 */
export const GAME_EVENTS_LAYER_QUEUE_MAX = 500;
/** Per game, per UTC day. What it drops is counted and shown as a fact, never with a reason. */
export const MAX_EVENTS_PER_GAME_PER_DAY = 200_000;
/** A longer attempt is clamped at ingest and flagged — a tester left a level paused over lunch. */
export const MAX_ATTEMPT_HOURS = 6;
/** Raw events are kept this long; `daily` and `player_units` for the life of the game (§12.1). */
export const GAME_EVENT_RETENTION_DAYS = 90;
/** A stored catalog, serialized. Far beyond 200 well-described events. */
export const MAX_EVENT_CATALOG_BYTES = 256 * 1024;
/** An event's description: required, and long enough to say when it fires (§5.5). */
export const EVENT_DESCRIPTION_MIN = 20;
export const EVENT_DESCRIPTION_MAX = 400;
/** A property's or an option's description: optional, shorter. */
export const DETAIL_DESCRIPTION_MAX = 200;
/** Bits of `events.flags` in a game's store (§9.5). */
export const GAME_EVENT_FLAGS = {
    /** No catalog entry when it arrived. Stored, counted, offered for declaration. */
    undeclared: 1,
    /** A property's type disagreed with the catalog. Left out of that property's charts. */
    mismatch: 2,
    /** A timestamp in the future or a duration over `MAX_ATTEMPT_HOURS`, pulled back. */
    clamped: 4,
    /**
     * The batch's `channelStamp` named a different channel from the one the build is published to
     * (§6.4): a build stamped `paid` reporting from `free`. Detection, not prevention.
     */
    stampDiffers: 8,
};
/** How far ahead of the server a device's clock may be before its timestamps are pulled back. */
export const GAME_EVENT_FUTURE_SKEW_MS = 5 * 60 * 1000;
/* ---------------------------------------------------------------------------------- kinds */
/**
 * The seven kinds (§2). A kind is a contract between the SDK (which helper emits it and what it
 * measures for free), the catalog (which properties have which role) and the dashboard (which
 * report it gets). The order here is the order the Events section shows them in.
 */
export const GAME_EVENT_KINDS = [
    'progress',
    'choice',
    'outcome',
    'value',
    'milestone',
    'action',
    'economy',
];
export const GameEventKind = z.enum(GAME_EVENT_KINDS);
/** What each kind is for, in one line — the catalog editor's legend and the skill's table. */
export const GAME_EVENT_KIND_SUMMARY = {
    progress: 'An attempt at a unit of content (a level, stage, puzzle or wave) that is completed, failed or quit.',
    choice: 'One option picked from a known set (hero class, difficulty, mode), carried by later events as context.',
    outcome: 'The result of a match or round, for the table and for each seat.',
    value: 'A number observed at a moment: moves to solve, coins at the end, combo length.',
    milestone: 'A once-per-player first: tutorial done, first win, world 2 unlocked.',
    action: 'Something that happens many times: hint used, undo, rules opened.',
    economy: 'A resource flowing in or out: gold, gems, lives, cards.',
};
/** Phases an event may carry: an attempt's lifecycle, and an outcome's two rows. */
export const GameEventPhase = z.enum(['start', 'complete', 'fail', 'quit', 'table', 'seat']);
/* -------------------------------------------------------------------------------- catalog */
const Identifier = z
    .string()
    .regex(EVENT_STRING_PATTERN, 'Must be an identifier: letters, digits and _ . : - only, up to 48 characters.');
const PropName = z
    .string()
    .regex(EVENT_PROP_PATTERN, 'Must start with a lowercase letter, then letters, digits or _, up to 40 characters.');
const Label = z.string().trim().min(1).max(60);
const DetailDescription = z.string().trim().min(1).max(DETAIL_DESCRIPTION_MAX);
/**
 * Required, 20–400 characters. **An agent proposing events writes these itself**, from the code it
 * read, and never leaves one for the developer (decision 8).
 */
const EventDescription = z
    .string({ error: 'Every event needs a description: when it fires, what ends it, and what its properties mean.' })
    .trim()
    .min(EVENT_DESCRIPTION_MIN, `A description says when the event fires and what it counts, in at least ${EVENT_DESCRIPTION_MIN} characters.`)
    .max(EVENT_DESCRIPTION_MAX);
/** A choice option's or a string property's value → how the dashboard shows it. */
export const EventValueLabel = z.union([
    Label,
    z.object({ label: Label, description: DetailDescription.optional() }).strict(),
]);
const ValueLabels = z
    .record(Identifier, EventValueLabel)
    .refine((values) => Object.keys(values).length <= 100, 'At most 100 values.');
/**
 * What a property is *for*, where a kind's report needs to know: the stars to stack, the score to
 * histogram. Everything a kind needs that is not a property — a progress unit, a choice's options,
 * an outcome's result — is a field of the kind instead.
 */
export const EventPropRole = z.enum(['rating', 'score']);
export const EventPropDefinition = z
    .object({
    type: z.enum(['int', 'number', 'boolean', 'string']),
    label: Label.optional(),
    description: DetailDescription.optional(),
    role: EventPropRole.optional(),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    /** For a string property: the identifiers it takes, and their labels. */
    values: ValueLabels.optional(),
})
    .strict()
    .superRefine((prop, ctx) => {
    if (prop.role === 'rating' && (prop.type !== 'int' || prop.max === undefined || prop.max > 10)) {
        ctx.addIssue({
            code: 'custom',
            path: ['role'],
            message: 'A rating is an int with a max of 10 or less, like stars, charted as one bar per value.',
        });
    }
    if (prop.role === 'score' && prop.type !== 'int' && prop.type !== 'number') {
        ctx.addIssue({ code: 'custom', path: ['role'], message: 'A score is a number.' });
    }
    if (prop.values && prop.type !== 'string') {
        ctx.addIssue({ code: 'custom', path: ['values'], message: 'Only a string property lists values.' });
    }
    if ((prop.min !== undefined || prop.max !== undefined) && prop.type !== 'int' && prop.type !== 'number') {
        ctx.addIssue({ code: 'custom', path: ['min'], message: 'Only a number has a range.' });
    }
    if (prop.min !== undefined && prop.max !== undefined && prop.min > prop.max) {
        ctx.addIssue({ code: 'custom', path: ['min'], message: 'min is above max.' });
    }
});
const Props = z
    .record(PropName, EventPropDefinition)
    .refine((props) => Object.keys(props).length <= MAX_EVENT_PROPS, `At most ${MAX_EVENT_PROPS} properties.`);
/** Fields every kind has. */
const common = {
    label: Label.optional(),
    description: EventDescription,
    props: Props.optional(),
};
const ProgressEvent = z
    .object({
    kind: z.literal('progress'),
    ...common,
    /** Which property names the unit, and the units in the order a player meets them. */
    unit: z
        .object({
        prop: PropName,
        order: z.array(Identifier).max(500).optional(),
        labels: ValueLabels.optional(),
    })
        .strict(),
})
    .strict();
const ChoiceEvent = z
    .object({
    kind: z.literal('choice'),
    ...common,
    /** How long a pick stays in force, and so which later events carry it as context (§2.2). */
    scope: z.enum(['match', 'session', 'player']),
    option: z
        .object({
        values: ValueLabels.refine((values) => Object.keys(values).length > 0, 'A choice needs at least one option.'),
    })
        .strict(),
})
    .strict();
const OutcomeEvent = z
    .object({
    kind: z.literal('outcome'),
    ...common,
    /** `win_loss` is win, loss or draw per seat; `placement` is a rank per seat, 1 first. */
    result: z.enum(['win_loss', 'placement']),
})
    .strict();
const ValueEvent = z
    .object({
    kind: z.literal('value'),
    ...common,
    unit: z.enum(['count', 'ms', 's', 'points']),
    /** Histogram bucket edges, ascending. Absent: log₂ buckets. */
    buckets: z.array(z.number().finite()).min(1).max(20).optional(),
})
    .strict()
    .superRefine((event, ctx) => {
    const buckets = event.buckets ?? [];
    if (buckets.some((edge, index) => index > 0 && edge <= buckets[index - 1])) {
        ctx.addIssue({ code: 'custom', path: ['buckets'], message: 'Bucket edges must be ascending.' });
    }
});
const MilestoneEvent = z
    .object({
    kind: z.literal('milestone'),
    ...common,
    /** Position in the milestone funnel, when milestones form one. */
    order: z.number().int().min(1).max(100).optional(),
})
    .strict();
const ActionEvent = z.object({ kind: z.literal('action'), ...common }).strict();
const EconomyEvent = z
    .object({
    kind: z.literal('economy'),
    ...common,
    currency: Identifier,
})
    .strict();
export const EventDefinition = z.discriminatedUnion('kind', [
    ProgressEvent,
    ChoiceEvent,
    OutcomeEvent,
    ValueEvent,
    MilestoneEvent,
    ActionEvent,
    EconomyEvent,
]);
/**
 * A funnel step: `event`, `event:subject` or `event:subject:phase` — `level:1-1:complete`,
 * `match:win`, `tutorial_done`.
 */
export const FUNNEL_STEP_PATTERN = /^[a-z][a-z0-9_]{0,39}(:[A-Za-z0-9][A-Za-z0-9_.\-]{0,47}){0,2}$/;
/**
 * `boardflight.events.json` (§5.1), and what `PUT /v1/apps/:appId/event-catalog` stores.
 *
 * Unknown keys are refused rather than ignored, everywhere but `$schema`: a catalog is written by
 * people and agents, and `"descripton"` silently ignored would be an event without a description
 * that looked like it had one.
 */
export const EventCatalog = z
    .object({
    $schema: z.string().max(200).optional(),
    events: z
        .record(z.string().regex(EVENT_NAME_PATTERN, 'An event name is lower snake case: a–z, 0–9 and _, up to 40 characters, starting with a letter.'), EventDefinition)
        .refine((events) => Object.keys(events).length <= MAX_EVENT_NAMES_PER_GAME, `At most ${MAX_EVENT_NAMES_PER_GAME} events.`),
    funnels: z
        .record(z.string().regex(EVENT_NAME_PATTERN), z.array(z.string().regex(FUNNEL_STEP_PATTERN, 'A step is event, event:subject or event:subject:phase.')).min(2).max(10))
        .optional(),
})
    .strict()
    .superRefine((catalog, ctx) => {
    for (const [funnel, steps] of Object.entries(catalog.funnels ?? {})) {
        steps.forEach((step, index) => {
            const event = step.split(':')[0];
            if (!(event in catalog.events)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['funnels', funnel, index],
                    message: `"${event}" is not declared in events.`,
                });
            }
        });
    }
});
/**
 * `boardflight.events.json`'s published JSON Schema, served at
 * `https://boardflight.app/schemas/events-v1.json` so editors and agents validate as they type
 * (§5.1). Generated from `EventCatalog` itself, so the file and the server's check cannot disagree;
 * `apps/web/test/event-schema.test.ts` fails when the committed copy is stale.
 */
export function eventCatalogJsonSchema() {
    return {
        ...z.toJSONSchema(EventCatalog, { io: 'input', unrepresentable: 'any' }),
        $id: 'https://boardflight.app/schemas/events-v1.json',
        title: 'BoardFlight game events catalog',
        description: 'The events a Board game reports through @boardflight/game. Every event needs a description: when it fires, what ends it and what its properties count (docs/game-events-plan.md §5).',
    };
}
/** The catalog a game with none has. */
export const EMPTY_EVENT_CATALOG = { events: {} };
/**
 * A refused catalog's problems, one sentence each, naming the event.
 *
 * `PUT …/event-catalog` puts these in its 400, so a person and an agent both see "level:
 * description — Every event needs a description…" rather than a zod path.
 */
export function eventCatalogProblems(error) {
    return error.issues.map((issue) => {
        // Inside a request body the catalog sits under `catalog`; the reader cares about the event.
        const raw = issue.path.map(String);
        const path = raw[0] === 'catalog' ? raw.slice(1) : raw;
        if (path[0] === 'events' && path.length >= 2) {
            const rest = path.slice(2).join('.');
            return rest ? `${path[1]}, ${rest}: ${issue.message}` : `${path[1]}: ${issue.message}`;
        }
        return path.length > 0 ? `${path.join('.')}: ${issue.message}` : issue.message;
    });
}
/** Property names that look like a person rather than a game. A warning, not a refusal (§5.4). */
const PERSONAL = new Set([
    'name', 'nick', 'nickname', 'username', 'user_name', 'player_name', 'playername', 'first_name',
    'firstname', 'last_name', 'lastname', 'full_name', 'fullname', 'real_name', 'email', 'phone',
    'address', 'birthday', 'birthdate', 'birth_date', 'dob', 'age',
]);
function looksPersonal(prop) {
    const lower = prop.toLowerCase();
    return PERSONAL.has(lower) || lower.includes('email') || lower.includes('phone');
}
/** Words that say nothing about an event beyond its name. */
const FILLER = new Set([
    'a', 'an', 'the', 'is', 'are', 'was', 'when', 'this', 'that', 'event', 'events', 'fired', 'fires',
    'fire', 'triggered', 'trigger', 'triggers', 'sent', 'send', 'happens', 'occurs', 'player',
    'players', 'game', 'of', 'for', 'on', 'in', 'to', 'it', 'gets', 'logged', 'logs', 'tracks',
    'tracked', 'records', 'recorded',
]);
const words = (text) => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
/**
 * The linter agents, the CLI and the dashboard all run (§11.1, §5.5). It cannot judge prose, so
 * everything here is a warning: a catalog it complains about still saves.
 */
export function lintEventCatalog(catalog) {
    const warnings = [];
    const milestoneOrders = new Map();
    for (const [name, event] of Object.entries(catalog.events)) {
        const props = event.props ?? {};
        const description = words(event.description);
        const mentions = (term) => {
            const needle = words(term).join(' ');
            return needle.length > 0 && ` ${description.join(' ')} `.includes(` ${needle} `);
        };
        // "level — the level event." Every content word is already in the name or the label.
        const known = new Set([...words(name), ...words(event.label ?? ''), ...FILLER]);
        if (description.every((word) => known.has(word))) {
            warnings.push({
                event: name,
                code: 'description_restates_name',
                message: `${name}: the description only restates the name. Say when it fires, what ends it and what its properties count.`,
            });
        }
        for (const [prop, definition] of Object.entries(props)) {
            if (looksPersonal(prop)) {
                warnings.push({
                    event: name,
                    code: 'personal_data',
                    message: `${name}.${prop} looks like personal data. Events must never carry a player's name or contact details.`,
                });
            }
            if (definition.type === 'string' && !definition.values && !(event.kind === 'progress' && event.unit.prop === prop)) {
                warnings.push({
                    event: name,
                    code: 'string_without_values',
                    message: `${name}.${prop} is a string with no declared values, so its chart shows raw identifiers.`,
                });
            }
            if (definition.role && !definition.description && !mentions(prop) && !mentions(definition.label ?? '')) {
                warnings.push({
                    event: name,
                    code: 'role_not_described',
                    message: `${name}.${prop} is the ${definition.role}, but neither the event's description nor its own says what it counts.`,
                });
            }
        }
        if (event.kind === 'progress' && !event.unit.order) {
            warnings.push({
                event: name,
                code: 'unit_without_order',
                message: `${name}: no unit order, so the ladder and the reach curve sort units alphabetically.`,
            });
        }
        if (event.kind === 'milestone' && event.order !== undefined) {
            const other = milestoneOrders.get(event.order);
            if (other) {
                warnings.push({
                    event: name,
                    code: 'milestone_order_repeated',
                    message: `${name} and ${other} both have order ${event.order}, so the milestone funnel cannot tell which comes first.`,
                });
            }
            else {
                milestoneOrders.set(event.order, name);
            }
        }
    }
    return warnings;
}
/* ---------------------------------------------------------------------------- catalog API */
/** Who wrote a catalog version. The uploader's word, like a version name. */
export const EventCatalogSource = z.enum(['dashboard', 'cli', 'mcp', 'playground', 'unity']);
export const PutEventCatalogRequest = z.object({
    catalog: EventCatalog,
    source: EventCatalogSource.default('dashboard'),
    /**
     * The version the writer started from. Last write wins regardless (§5.2); this only lets the
     * response say what was replaced when somebody else wrote in between.
     */
    baseVersion: z.number().int().min(0).nullable().optional(),
});
export const EventCatalogVersion = z.object({
    version: z.number().int(),
    source: EventCatalogSource,
    author: UserIdentity.nullable(),
    createdAt: z.number().int(),
    events: z.number().int(),
});
export const EventCatalogResponse = z.object({
    appId: Ulid,
    /** The current catalog, or the requested version's. A game with none has `{ events: {} }`. */
    catalog: EventCatalog,
    /** Null for a game that has never had one. */
    version: z.number().int().nullable(),
    /** The linter's view of `catalog`. */
    warnings: z.array(z.object({ event: z.string().nullable(), code: z.string(), message: z.string() })),
    /** Newest first, at most 50. */
    versions: z.array(EventCatalogVersion),
    /** On a write: the version this one replaced, when it was not the writer's `baseVersion`. */
    replaced: EventCatalogVersion.nullable().optional(),
    /** On a write: the catalog was identical to the current one, so no version was made. */
    unchanged: z.boolean().optional(),
});
/**
 * One stable serialization of a **parsed** catalog, so "identical to the current version" is a
 * string comparison.
 *
 * Not key-sorted, deliberately. Zod's output puts fixed fields in schema order, and names and
 * values keep the writer's order — which means something: a choice's options and a string's values
 * are shown in the order they were declared, so *easy, normal, hard* must not become *easy, hard,
 * normal*. Reordering them is a change, and makes a version.
 */
export function canonicalEventCatalog(catalog) {
    return JSON.stringify(EventCatalog.parse(catalog));
}
/* ---------------------------------------------------------------------------- the envelope */
/**
 * A seat, as the layer sends it: never Board's id, never a name (§4).
 *
 * `k` is `sha256(app_salt ‖ profile id)` for a profile and `sha256(session_id ‖ guest id)` for a
 * guest, hex. An AI seat has no key.
 */
export const GameEventPlayer = z
    .object({
    k: z.string().regex(/^[0-9a-f]{64}$/).optional(),
    t: z.enum(['profile', 'guest', 'ai']),
    s: z.number().int().min(0).max(15).optional(),
})
    .strict();
export const GameEventPropValue = z.union([
    z.string().regex(EVENT_STRING_PATTERN),
    z.number().finite().min(-MAX_EVENT_NUMBER).max(MAX_EVENT_NUMBER),
    z.boolean(),
]);
const bounded = (schema, max) => schema.refine((value) => Object.keys(value).length <= max, `At most ${max} entries.`);
/**
 * One event as the layer sends it (§7.3).
 *
 * Parsed **one at a time**, not as part of the batch: an event that fails here is dropped and
 * counted, and the rest of its batch is kept. A whole playtest's evening must not be refused
 * because one call site sent a sentence.
 */
export const GameEvent = z
    .object({
    /** The layer's per-install counter. Orders a device's events; the clock is trusted for nothing. */
    seq: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    /** Device clock, ms. Clamped at ingest. */
    at: z.number().int().min(0),
    name: z.string().regex(EVENT_NAME_PATTERN),
    phase: GameEventPhase.optional(),
    player: GameEventPlayer.optional(),
    /** Active time, measured by the SDK with hidden time excluded: an attempt's, a match's. */
    durationMs: z.number().int().min(0).optional(),
    /** The SDK's attempt number for this player and unit, 1 first. */
    attempt: z.number().int().min(1).max(1_000_000).optional(),
    /** Sticky choices in force (§2.2). */
    ctx: bounded(z.record(z.string().regex(EVENT_PROP_PATTERN), z.string().regex(EVENT_STRING_PATTERN)), MAX_EVENT_PROPS).optional(),
    props: bounded(z.record(z.string().regex(EVENT_PROP_PATTERN), GameEventPropValue), MAX_EVENT_PROPS).default({}),
})
    .strict();
/** `POST /v1/analytics/events` (§8.2). The events are checked one by one — see `GameEvent`. */
export const GameEventsIngestRequest = z.object({
    /** The game's ingest key, baked into the build. An identifier, not a credential. */
    key: z.string().min(1).max(200),
    buildId: Ulid,
    installId: Uuid,
    sessionId: Uuid.optional(),
    platform: z.enum(['unity', 'web']),
    /** The catalog version the SDK was built against. Informational. */
    catalogVersion: z.number().int().min(0).optional(),
    /** The channel slug the build was stamped with (§6.4). Compared with the build row, never trusted. */
    channelStamp: z.string().max(64).nullable().optional(),
    events: z.array(z.unknown()).min(1).max(MAX_GAME_EVENTS_PER_REQUEST),
    /**
     * The device's clock when this request was sent, on the same clock as every event's `at`. The
     * server moves the whole batch by the difference when it is past `CLOCK_SKEW_TOLERANCE_MS`.
     * Optional: layers patched before it existed do not send it.
     */
    sentAt: z.number().int().optional(),
    /**
     * Events this device threw away without sending since the last request the server answered —
     * its queue was full (`GAME_EVENTS_LAYER_QUEUE_MAX`). Counted on the day's ingest record, so a
     * game that outruns its queue is a number on the Events tab rather than a silence.
     */
    dropped: z.number().int().min(0).max(1_000_000).optional(),
});
/* ------------------------------------------------------------------------ the kind's fields */
/**
 * The property names a kind's SDK helper fills in, and what the store makes of them (§9.5).
 *
 * The envelope is deliberately generic — a name, a phase, a seat, properties — so a kind's own
 * numbers ride in reserved properties the SDK writes and the catalog never declares:
 *
 * | Kind      | Subject (what the event is *about*)       | Value (its number)                   |
 * |-----------|-------------------------------------------|--------------------------------------|
 * | progress  | the catalog's `unit.prop`                 | `durationMs`, on complete/fail/quit  |
 * | choice    | `option`                                  | —                                    |
 * | outcome   | `result` (win/loss/draw) or `placement`   | `score`, else `durationMs` (table)   |
 * | value     | —                                         | `value`                              |
 * | milestone | —                                         | —                                    |
 * | action    | —                                         | —                                    |
 * | economy   | the catalog's `currency`                  | `amount`, negative for a `sink` flow |
 *
 * They count towards the sixteen properties like any other, and they are reserved only within
 * their kind: an `action` may have a property called `value`.
 */
export const KIND_FIELDS = {
    choice: ['option'],
    outcome: ['result', 'placement', 'score'],
    value: ['value'],
    economy: ['amount', 'flow'],
};
/** Which phases each kind sends. Anything else on a declared event is a mismatch. */
const PHASES = {
    progress: ['start', 'complete', 'fail', 'quit'],
    choice: [undefined],
    outcome: ['table', 'seat'],
    value: [undefined],
    milestone: [undefined],
    action: [undefined],
    economy: [undefined],
};
const typeMatches = (type, value) => type === 'string'
    ? typeof value === 'string'
    : type === 'boolean'
        ? typeof value === 'boolean'
        : type === 'int'
            ? Number.isInteger(value)
            : typeof value === 'number';
/**
 * Check one parsed event against the game's catalog — the one implementation the Worker's ingest,
 * agentd's dev sink and `events_check` and the dashboard's console all call, so "mismatched"
 * means the same thing in the preview as it does on a Board.
 *
 * **Never a refusal.** An undeclared event and a mistyped property are stored and flagged (§5.3);
 * what refuses an event is `GameEvent` itself, before this runs.
 */
export function checkGameEvent(event, catalog, now) {
    let flags = 0;
    const problems = [];
    let at = event.at;
    if (at > now + GAME_EVENT_FUTURE_SKEW_MS) {
        at = now;
        flags |= GAME_EVENT_FLAGS.clamped;
        problems.push('Its time was in the future, so it was set to now.');
    }
    let durationMs = event.durationMs ?? null;
    if (durationMs !== null && durationMs > MAX_ATTEMPT_HOURS * 3_600_000) {
        durationMs = MAX_ATTEMPT_HOURS * 3_600_000;
        flags |= GAME_EVENT_FLAGS.clamped;
        problems.push(`It lasted more than ${MAX_ATTEMPT_HOURS} hours, so it counts as ${MAX_ATTEMPT_HOURS}.`);
    }
    const definition = catalog.events[event.name];
    if (!definition) {
        return {
            kind: null,
            subject: null,
            value: null,
            flags: flags | GAME_EVENT_FLAGS.undeclared,
            at,
            durationMs,
            problems: [...problems, `"${event.name}" is not declared in the catalog.`],
        };
    }
    const mismatch = (problem) => {
        flags |= GAME_EVENT_FLAGS.mismatch;
        problems.push(problem);
    };
    if (!PHASES[definition.kind].includes(event.phase)) {
        mismatch(event.phase
            ? `A ${definition.kind} event has no "${event.phase}" phase.`
            : `A ${definition.kind} event needs a phase: ${PHASES[definition.kind].join(', ')}.`);
    }
    for (const [prop, propDefinition] of Object.entries(definition.props ?? {})) {
        const value = event.props[prop];
        if (value === undefined)
            continue;
        if (!typeMatches(propDefinition.type, value)) {
            mismatch(`${prop} should be ${propDefinition.type === 'int' ? 'an int' : `a ${propDefinition.type}`}.`);
        }
        else if (propDefinition.values && typeof value === 'string' && !(value in propDefinition.values)) {
            mismatch(`${prop} "${value}" is not one of its declared values.`);
        }
    }
    const str = (key) => (typeof event.props[key] === 'string' ? event.props[key] : null);
    const num = (key) => (typeof event.props[key] === 'number' ? event.props[key] : null);
    let subject = null;
    let value = null;
    switch (definition.kind) {
        case 'progress': {
            subject = str(definition.unit.prop) ?? (num(definition.unit.prop)?.toString() ?? null);
            if (subject === null)
                mismatch(`A progress event names its unit in "${definition.unit.prop}".`);
            else if (definition.unit.order && !definition.unit.order.includes(subject)) {
                mismatch(`Unit "${subject}" is not in the catalog's order.`);
            }
            value = event.phase === 'start' ? null : durationMs;
            break;
        }
        case 'choice': {
            subject = str('option');
            if (subject === null)
                mismatch('A choice names the picked option in "option".');
            else if (!(subject in definition.option.values))
                mismatch(`"${subject}" is not one of the declared options.`);
            break;
        }
        case 'outcome': {
            if (event.phase === 'seat') {
                if (definition.result === 'placement') {
                    const placement = num('placement');
                    subject = placement === null ? null : String(placement);
                    if (placement === null || !Number.isInteger(placement) || placement < 1) {
                        mismatch('A placement outcome gives each seat a "placement", 1 first.');
                    }
                }
                else {
                    subject = str('result');
                    if (subject !== 'win' && subject !== 'loss' && subject !== 'draw') {
                        mismatch('A win/loss outcome gives each seat a "result": win, loss or draw.');
                    }
                }
            }
            value = num('score') ?? (event.phase === 'table' ? durationMs : null);
            break;
        }
        case 'value': {
            value = num('value');
            if (value === null)
                mismatch('A value event carries its number in "value".');
            break;
        }
        case 'economy': {
            subject = definition.currency;
            const amount = num('amount');
            const flow = str('flow');
            if (amount === null)
                mismatch('An economy event carries "amount".');
            if (flow !== null && flow !== 'source' && flow !== 'sink')
                mismatch('"flow" is source or sink.');
            value = amount === null ? null : flow === 'sink' ? -Math.abs(amount) : amount;
            break;
        }
        default:
            break;
    }
    return { kind: definition.kind, subject, value, flags, at, durationMs, problems };
}
/* ------------------------------------------------------------------------- the reading end */
/** One stored event, as *Recent events* and agentd's tools show it (§10.2). */
export const StoredGameEvent = z.object({
    name: z.string(),
    kind: GameEventKind.nullable(),
    phase: GameEventPhase.nullable(),
    subject: z.string().nullable(),
    value: z.number().nullable(),
    at: z.number().int(),
    receivedAt: z.number().int(),
    channelId: z.string(),
    buildId: z.string(),
    /** The first 8 hex characters of the hashed key: enough to tell seats apart, nothing more. */
    player: z.string().nullable(),
    actor: z.enum(['profile', 'guest', 'ai']).nullable(),
    seat: z.number().int().nullable(),
    props: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
    ctx: z.record(z.string(), z.string()),
    flags: z.number().int(),
});
/** One UTC day of a game's ingest, as BoardFlight counts it outside the game's store (§9.5). */
export const GameEventIngestDay = z.object({
    day: z.string(),
    accepted: z.number().int(),
    droppedCap: z.number().int(),
    droppedInvalid: z.number().int(),
    /** Thrown away on the device because its queue was full, as the layers report it. */
    droppedDevice: z.number().int(),
    undeclared: z.number().int(),
    mismatched: z.number().int(),
    lastAt: z.number().int().nullable(),
});
/** `GET /v1/apps/:appId/events/recent`. */
export const RecentGameEventsResponse = z.object({
    /** Newest first. */
    items: z.array(StoredGameEvent),
    /** Today's ingest for the whole game (UTC), or null before anything arrived today. */
    today: GameEventIngestDay.nullable(),
});
/* ------------------------------------------------------------------------------ reports */
/**
 * Below this many, a rate is drawn greyed as *too few to tell* and never coloured (§3.3). A playtest
 * has ten players, not ten thousand, and "Mage: 100% win rate" from two matches is worse than no
 * chart.
 */
export const MIN_RATE_N = 10;
/** A difficulty spike is only claimed with at least this many players attempting both units (§3.4). */
export const SPIKE_MIN_PLAYERS = 20;
/** A report shows a string property's top values and folds the rest into *(other)* (§5.4). */
export const REPORT_TOP_VALUES = 50;
/** A rate, always with its denominator and a 95% Wilson interval (§3.3). Null rate when `n` is 0. */
export const RateValue = z.object({
    k: z.number().int(),
    n: z.number().int(),
    rate: z.number().nullable(),
    low: z.number().nullable(),
    high: z.number().nullable(),
});
/** The Wilson score interval for k of n at 95%. */
export function wilson(k, n) {
    if (n <= 0)
        return { k, n, rate: null, low: null, high: null };
    const z = 1.959963984540054;
    const p = k / n;
    const denominator = 1 + (z * z) / n;
    const centre = (p + (z * z) / (2 * n)) / denominator;
    const margin = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / denominator;
    return { k, n, rate: p, low: Math.max(0, centre - margin), high: Math.min(1, centre + margin) };
}
/**
 * Who a report's per-player figures count (§4), decided per event (`useIdentity`): Board players;
 * the game's installs, for a game that never names a player, where a device is the only "who" there
 * is; or nobody — `table` — for an event a game sends without a player while naming players
 * elsewhere. A table's event belongs to everybody at it, so its report is read in tries, picks and
 * sessions, and a funnel cannot follow anybody through it.
 */
export const EventWho = z.enum(['player', 'install', 'table']);
/** Whether two rates differ by the only claim this product makes: intervals that do not overlap. */
export function ratesDiffer(a, b) {
    if (a.n < MIN_RATE_N || b.n < MIN_RATE_N || a.low === null || b.low === null)
        return false;
    return a.high < b.low || b.high < a.low;
}
/** Exact percentiles by rank, nearest-rank method. Every field null when there are no values. */
export const Percentiles = z.object({
    n: z.number().int(),
    min: z.number().nullable(),
    p10: z.number().nullable(),
    p25: z.number().nullable(),
    p50: z.number().nullable(),
    p75: z.number().nullable(),
    p90: z.number().nullable(),
    max: z.number().nullable(),
    mean: z.number().nullable(),
});
export function percentilesOf(values) {
    const sorted = [...values].filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
    const n = sorted.length;
    const rank = (p) => (n === 0 ? null : sorted[Math.min(n - 1, Math.max(0, Math.ceil((p / 100) * n) - 1))]);
    return {
        n,
        min: n ? sorted[0] : null,
        p10: rank(10),
        p25: rank(25),
        p50: rank(50),
        p75: rank(75),
        p90: rank(90),
        max: n ? sorted[n - 1] : null,
        mean: n ? sorted.reduce((sum, value) => sum + value, 0) / n : null,
    };
}
export const HistogramBin = z.object({
    label: z.string(),
    from: z.number().nullable(),
    to: z.number().nullable(),
    count: z.number().int(),
});
/**
 * A histogram: the catalog's bucket edges when it has them, else log₂ buckets for anything that
 * spans orders of magnitude, else one bar per integer for a small range (§3.1).
 */
export function histogramOf(values, edges) {
    const finite = values.filter((value) => Number.isFinite(value));
    if (finite.length === 0)
        return [];
    let cuts = edges && edges.length > 0 ? [...edges] : null;
    const max = Math.max(...finite);
    const min = Math.min(...finite);
    if (!cuts) {
        if (Number.isInteger(min) && Number.isInteger(max) && min >= 0 && max <= 10) {
            return Array.from({ length: max - min + 1 }, (_, i) => min + i).map((value) => ({
                label: String(value),
                from: value,
                to: value + 1,
                count: finite.filter((v) => v === value).length,
            }));
        }
        cuts = [];
        for (let edge = 1; edge <= Math.max(1, max); edge *= 2)
            cuts.push(edge);
    }
    const bins = [];
    let previous = null;
    for (const edge of cuts) {
        bins.push({
            label: previous === null ? `< ${edge}` : `${previous}–${edge}`,
            from: previous,
            to: edge,
            count: finite.filter((value) => (previous === null || value >= previous) && value < edge).length,
        });
        previous = edge;
    }
    bins.push({ label: `≥ ${previous}`, from: previous, to: null, count: finite.filter((value) => value >= previous).length });
    return bins;
}
const DayCount = z.object({ day: z.string(), count: z.number().int() });
const ProgressUnitReport = z.object({
    unit: z.string(),
    label: z.string(),
    starts: z.number().int(),
    players: z.number().int(),
    completes: z.number().int(),
    fails: z.number().int(),
    quits: z.number().int(),
    /** Completed of ended attempts. */
    completion: RateValue,
    /** Players whose first ended attempt was a completion, of players with one (per player, §3.2). */
    firstTry: RateValue,
    /** Players who cleared it, of players who played this event at all. */
    reach: RateValue,
    /** Per player, then across players (§3.2). */
    attemptsToClear: Percentiles,
    /** Active milliseconds per completed attempt. */
    timeToClear: Percentiles,
    ratings: z.array(z.object({ value: z.number().int(), count: z.number().int() })).nullable(),
    score: Percentiles.nullable(),
    /** §3.4: this unit's completion is clearly below the previous unit's. */
    spike: z.boolean(),
});
const ProgressBody = z.object({
    kind: z.literal('progress'),
    unitProp: z.string(),
    ratingMax: z.number().nullable(),
    units: z.array(ProgressUnitReport),
});
const OptionRate = z.object({ option: z.string(), label: z.string(), rate: RateValue });
const ChoiceBody = z.object({
    kind: z.literal('choice'),
    options: z.array(z.object({
        option: z.string(),
        label: z.string(),
        picks: z.number().int(),
        players: z.number().int(),
        /** Players who picked it, of players who picked anything (per player, §3.2). */
        share: RateValue,
    })),
    /** Outcome and progress events that carry this pick in context, split by option (§2.1). */
    balance: z.array(z.object({
        event: z.string(),
        label: z.string(),
        measure: z.string(),
        options: z.array(OptionRate),
    })),
});
const GroupRate = z.object({ group: z.string(), rate: RateValue });
const OutcomeBody = z.object({
    kind: z.literal('outcome'),
    result: z.enum(['win_loss', 'placement']),
    matches: z.number().int(),
    length: Percentiles,
    drawRate: RateValue.nullable(),
    bySeat: z.array(GroupRate),
    byPlayers: z.array(GroupRate),
    humanVsAi: z.array(GroupRate),
    byContext: z.array(z.object({ key: z.string(), groups: z.array(GroupRate) })),
    score: Percentiles.nullable(),
});
const ValueBody = z.object({
    kind: z.literal('value'),
    unit: z.string(),
    values: Percentiles,
    histogram: z.array(HistogramBin),
    perDay: z.array(z.object({ day: z.string(), p50: z.number().nullable(), n: z.number().int() })),
});
const MilestoneBody = z.object({
    kind: z.literal('milestone'),
    reach: RateValue,
    /**
     * Play sessions it happened in, of every session in the window: what a table's milestone is read
     * in, since nobody reaches one. Optional only for a Worker deployed before it was counted.
     */
    sessions: RateValue.optional(),
    /** Milliseconds from a player's first event in the window to their first time reaching this. */
    timeToReach: Percentiles,
    funnel: z.array(z.object({ name: z.string(), label: z.string(), reached: z.number().int(), conversion: RateValue, who: EventWho.optional() })),
});
const Breakdown = z.object({
    key: z.string(),
    values: z.array(z.object({ value: z.string(), count: z.number().int(), players: z.number().int() })),
});
const ActionBody = z.object({
    kind: z.literal('action'),
    count: z.number().int(),
    sessionsUsing: RateValue,
    perSession: Percentiles,
    byDimension: z.array(Breakdown),
});
const EconomyBody = z.object({
    kind: z.literal('economy'),
    currency: z.string(),
    sources: z.number(),
    sinks: z.number(),
    net: z.number(),
    perDay: z.array(z.object({ day: z.string(), sources: z.number(), sinks: z.number() })),
    bySource: z.array(z.object({ key: z.string(), values: z.array(z.object({ value: z.string(), amount: z.number() })) })),
});
const UndeclaredBody = z.object({
    kind: z.literal('undeclared'),
    count: z.number().int(),
    props: z.array(z.object({ name: z.string(), types: z.array(z.string()) })),
});
export const EventReportBody = z.discriminatedUnion('kind', [
    ProgressBody,
    ChoiceBody,
    OutcomeBody,
    ValueBody,
    MilestoneBody,
    ActionBody,
    EconomyBody,
    UndeclaredBody,
]);
export const ReportScope = z.object({
    from: z.string(),
    to: z.string(),
    channelId: z.string().nullable(),
    buildIds: z.array(z.string()),
});
const ReportCore = z.object({
    name: z.string(),
    kind: GameEventKind.nullable(),
    label: z.string(),
    /** The catalog's description, so a reader — person or agent — is told what this counts (§5.5). */
    description: z.string().nullable(),
    props: z.array(z.object({
        name: z.string(),
        type: z.string(),
        role: z.string().nullable(),
        description: z.string().nullable(),
    })),
    scope: ReportScope,
    /**
     * Who the per-player figures count. Optional only because a dashboard can reach a Worker deployed
     * before it carried one, which counted every event of a game that names players by player.
     */
    who: EventWho.optional(),
    totals: z.object({
        events: z.number().int(),
        /** Board profiles and guest sessions, counted apart: one guest who played three times is not three people. */
        profiles: z.number().int(),
        guests: z.number().int(),
        devices: z.number().int(),
        sessions: z.number().int(),
        /** Kept out of every human statistic. */
        aiEvents: z.number().int(),
        clamped: z.number().int(),
        mismatched: z.number().int(),
    }),
    perDay: z.array(DayCount),
    body: EventReportBody,
    breakdown: Breakdown.nullable(),
});
/** `GET /v1/apps/:appId/events/report/:name`. */
export const EventReport = ReportCore.extend({
    /** The same report for the comparison scope, when one was asked for (§3.1, build compare). */
    compare: ReportCore.nullable(),
});
/** One card of the Events overview (§10.2). */
export const EventOverviewCard = z.object({
    name: z.string(),
    kind: GameEventKind.nullable(),
    label: z.string(),
    description: z.string().nullable(),
    count: z.number().int(),
    players: z.number().int(),
    /** Who `players` and the body's per-player figures count; optional as the report's is. */
    who: EventWho.optional(),
    perDay: z.array(DayCount),
    /** The kind's headline (§2.1), already in words — `eventHeadline` of the report below. */
    headline: z.string(),
    /**
     * The kind's report body, so the overview can chart and phrase each event without a request per
     * card. Optional, like the overview's `totals`, only because a dashboard can reach a Worker
     * deployed before it carried one.
     */
    body: EventReportBody.optional(),
});
export const EventsOverviewResponse = z.object({
    scope: ReportScope,
    /** Every event in the scope together: how many, from how many players and sessions. */
    totals: ReportCore.shape.totals.optional(),
    events: z.array(EventOverviewCard),
    undeclared: z.array(EventOverviewCard),
    funnels: z.array(z.object({
        name: z.string(),
        /**
         * A step whose event is a table's (`who: 'table'`) cannot be followed per player: it and every
         * step after it count nobody, with a null conversion, rather than a zero that reads as a drop.
         */
        steps: z.array(z.object({
            step: z.string(),
            reached: z.number().int(),
            conversion: RateValue,
            who: EventWho.optional(),
            /**
             * How often the step happened in the window, by anybody and in any order, and how many
             * players did it: what a funnel nobody can be followed through is read in instead.
             */
            times: z.number().int().optional(),
            players: z.number().int().optional(),
        })),
    })),
    /** The progress ladder of the first progress event, the one chart for "how far do players get". */
    ladder: EventReport.nullable(),
    /** Balance: every choice that has outcome or progress events carrying it. */
    balance: z.array(EventReport),
});
/**
 * What a query may filter or group by: a column, or a property or context key by name. A pattern,
 * not an escape, because these reach SQL as names.
 */
export const QUERY_KEY_PATTERN = /^(phase|subject|seat|actor|(props|ctx)\.[a-z][a-zA-Z0-9_]{0,39})$/;
export const QUERY_BY_PATTERN = /^(phase|subject|seat|actor|day|build|(props|ctx)\.[a-z][a-zA-Z0-9_]{0,39})$/;
/** `POST /v1/apps/:appId/events/query` — a structured query, never SQL (§10.5). */
export const EventQueryRequest = z.object({
    event: z.string().regex(EVENT_NAME_PATTERN),
    measure: z.enum(['count', 'unique_players', 'rate', 'percentiles', 'histogram', 'funnel', 'retention']),
    /** Which rows count. `phase`, `subject`, and `props.<name>` / `ctx.<name>` equal to an identifier. */
    filters: z.record(z.string().regex(QUERY_KEY_PATTERN), z.union([Identifier, z.number(), z.boolean()])).optional(),
    /** For `rate`: the filter that picks the numerator out of the filtered rows. */
    numerator: z.record(z.string().regex(QUERY_KEY_PATTERN), z.union([Identifier, z.number(), z.boolean()])).optional(),
    /** Group by `phase`, `subject`, `seat`, `actor`, `day`, `build`, or `props.<name>` / `ctx.<name>`. */
    by: z.string().regex(QUERY_BY_PATTERN).optional(),
    /** `funnel`: the steps, as catalog funnels spell them. */
    steps: z.array(z.string().regex(FUNNEL_STEP_PATTERN)).min(2).max(10).optional(),
    channel: Ulid.optional(),
    builds: z.array(Ulid).max(10).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export const EventQueryResponse = z.object({
    measure: z.string(),
    scope: ReportScope,
    /** Who `unique_players` and `retention` count for the queried event. */
    who: EventWho.optional(),
    rows: z.array(z.object({
        group: z.string().nullable(),
        value: z.number().nullable(),
        /** `funnel`: who the step counts, as an overview funnel's step says it. */
        who: EventWho.optional(),
        rate: RateValue.optional(),
        percentiles: Percentiles.optional(),
        histogram: z.array(HistogramBin).optional(),
    })),
});
/* ------------------------------------------------------------------------- suggest events */
/**
 * *Suggest events* (§10.4, §11.2): the prompt the catalog panel puts in the playground's composer,
 * versioned so a change to it is a change somebody can see. It lands as a draft; the developer
 * sends it. The proposal comes before any code, every event arrives with its description written,
 * and the agent proves the instrumentation in the preview.
 */
export const SUGGEST_EVENTS_PROMPT_VERSION = 1;
export function suggestEventsPrompt() {
    return [
        `Suggest game events for this game (BoardFlight suggest-events v${SUGGEST_EVENTS_PROMPT_VERSION}).`,
        '',
        "1. Read the game's code and GAME.md, and open the board-events skill.",
        '2. Propose a catalog of five to ten events worth measuring first. For each: its name and kind, its properties, a description you write from the code (when it fires, what ends it, what its properties count), and one line on why it is worth measuring. Show me the proposal before changing any code.',
        '3. When I agree, instrument it with @boardflight/game and write boardflight.events.json.',
        '4. Prove it in the preview with events_recent and events_check, and tell me what you saw.',
    ].join('\n');
}
/* ----------------------------------------------------------------------------- disclosure */
/**
 * What game events send, in the words a player is owed (§12.3). The settings panel and the Privacy
 * page both render this constant, so neither can drift from the other.
 */
export const EVENTS_DISCLOSURE = "With game events on, this channel's builds send BoardFlight the events the game's own code " +
    'reports (what happened, its numbers and identifiers) under a random install id, with each ' +
    'Board profile replaced by a one-way code that differs in every game. BoardFlight never receives ' +
    'player names, Board profile ids, free text, screen contents or input.';
//# sourceMappingURL=game-events.js.map